// src/pages/TermsOfServicePage.js
import React from 'react';
import { Container, Card } from 'react-bootstrap';

const TermsOfServicePage = () => {
	const lastUpdated = "October 26, 2025";

	return (
		<div className="terms-of-service-page">
			<Container className="py-5">
				<div className="text-center mb-5">
					<h1 className="text-white display-4 mb-3">Terms of Service</h1>
					<p className="lead text-white">
						Terms and conditions for using Banking Intelligence services
					</p>
					<p className="text-white">
						<small>Last updated: {lastUpdated}</small>
					</p>
				</div>

				<Card className="shadow-sm">
					<Card.Body className="p-5">
						<div className="terms-content">
							{/* Agreement */}
							<section className="mb-5">
								<h2 className="h3 mb-3">1. Agreement to Terms</h2>
								<p>
									Welcome to Banking Intelligence. These Terms of Service ("Terms", "Agreement") govern your use of
									our AI-powered financial insights platform and related services ("Service") operated by Banking Intelligence
									("us", "we", or "our").
								</p>
								<p>
									By accessing or using our Service, you agree to be bound by these Terms. If you disagree with any
									part of these terms, then you may not access the Service.
								</p>
								<p className="alert alert-info">
									<strong>Important:</strong> These terms constitute a legally binding agreement between you and Banking Intelligence.
									Please read them carefully.
								</p>
							</section>

							{/* Service Description */}
							<section className="mb-5">
								<h2 className="h3 mb-3">2. Description of Service</h2>
								<p>Banking Intelligence provides:</p>
								<ul>
									<li><strong>Financial Data Analysis:</strong> AI-powered insights from your connected bank accounts</li>
									<li><strong>Spending Analytics:</strong> Categorization, trends, and patterns in your financial behavior</li>
									<li><strong>Personalized Recommendations:</strong> Suggestions for budgeting, saving, and financial optimization</li>
									<li><strong>Interactive Chat:</strong> Natural language interface for querying your financial data</li>
									<li><strong>Data Visualization:</strong> Charts, graphs, and reports of your financial information</li>
									<li><strong>Account Aggregation:</strong> Secure connection to multiple financial institutions via Plaid</li>
								</ul>
								<p className="text-muted">
									<small>
										The Service is intended for personal financial management and educational purposes.
										It is not intended as professional financial, investment, or tax advice.
									</small>
								</p>
							</section>

							{/* Account Registration */}
							<section className="mb-5">
								<h2 className="h3 mb-3">3. Account Registration and Security</h2>

								<h4 className="h5 mb-2">3.1 Account Creation</h4>
								<p>To use our Service, you must:</p>
								<ul>
									<li>Be at least 18 years old</li>
									<li>Provide accurate and complete registration information</li>
									<li>Maintain and update your account information</li>
									<li>Have the legal authority to enter into this agreement</li>
								</ul>

								<h4 className="h5 mb-2 mt-4">3.2 Account Security</h4>
								<p>You are responsible for:</p>
								<ul>
									<li>Maintaining the confidentiality of your account credentials</li>
									<li>All activities that occur under your account</li>
									<li>Notifying us immediately of any unauthorized access</li>
									<li>Enabling two-factor authentication when available</li>
								</ul>

								<h4 className="h5 mb-2 mt-4">3.3 Account Termination</h4>
								<p>
									You may terminate your account at any time. We may suspend or terminate your account if you
									violate these Terms or engage in activities that harm our Service or other users.
								</p>
							</section>

							{/* Financial Data Connection */}
							<section className="mb-5">
								<h2 className="h3 mb-3">4. Financial Data Connection and Use</h2>

								<h4 className="h5 mb-2">4.1 Bank Account Connection</h4>
								<p>By connecting your bank accounts through Plaid, you:</p>
								<ul>
									<li>Authorize us to access your account information and transaction data</li>
									<li>Represent that you have the authority to provide this access</li>
									<li>Understand that we will use this data to provide our services</li>
									<li>Acknowledge that account connectivity may occasionally be interrupted</li>
								</ul>

								<h4 className="h5 mb-2 mt-4">4.2 Data Accuracy</h4>
								<p>
									While we strive for accuracy, we cannot guarantee that all financial data retrieved from third-party
									sources is complete or error-free. You should verify important information with your financial institutions.
								</p>

								<h4 className="h5 mb-2 mt-4">4.3 Data Usage Rights</h4>
								<p>You grant us the right to:</p>
								<ul>
									<li>Access and process your financial data to provide the Service</li>
									<li>Use AI and machine learning to analyze your data for personalized insights</li>
									<li>Store your data securely according to our Privacy Policy</li>
									<li>Generate aggregated, anonymized analytics for service improvement</li>
								</ul>
							</section>

							{/* AI Services Disclaimer */}
							<section className="mb-5">
								<h2 className="h3 mb-3">5. AI Services and Limitations</h2>

								<h4 className="h5 mb-2">5.1 Nature of AI Insights</h4>
								<p>Our AI-powered features:</p>
								<ul>
									<li>Provide automated analysis based on your financial data</li>
									<li>Generate insights, patterns, and recommendations</li>
									<li>Use machine learning algorithms that may evolve over time</li>
									<li>Are intended for informational and educational purposes</li>
								</ul>

								<h4 className="h5 mb-2 mt-4">5.2 Not Financial Advice</h4>
								<div className="alert alert-warning">
									<p><strong>Important Disclaimer:</strong> Banking Intelligence does not provide financial, investment,
										tax, or legal advice. Our insights and recommendations are:</p>
									<ul>
										<li>For informational purposes only</li>
										<li>Based solely on automated analysis of your data</li>
										<li>Not a substitute for professional financial consultation</li>
										<li>Not guaranteed to be accurate or suitable for your specific situation</li>
									</ul>
								</div>

								<h4 className="h5 mb-2 mt-4">5.3 AI Limitations</h4>
								<p>You understand that AI systems:</p>
								<ul>
									<li>May produce incorrect or misleading results</li>
									<li>Cannot account for all personal circumstances</li>
									<li>Require human judgment for important financial decisions</li>
									<li>May have biases or limitations in their training data</li>
								</ul>
							</section>

							{/* User Responsibilities */}
							<section className="mb-5">
								<h2 className="h3 mb-3">6. User Responsibilities and Prohibited Uses</h2>

								<h4 className="h5 mb-2">6.1 Acceptable Use</h4>
								<p>You agree to use the Service only for lawful purposes and in accordance with these Terms. You will not:</p>
								<ul>
									<li>Connect accounts that do not belong to you without proper authorization</li>
									<li>Attempt to reverse engineer or hack our systems</li>
									<li>Share your account with others or create accounts for others</li>
									<li>Use the Service to violate any applicable laws or regulations</li>
									<li>Interfere with the proper functioning of the Service</li>
								</ul>

								<h4 className="h5 mb-2 mt-4">6.2 Data Responsibility</h4>
								<p>You are responsible for:</p>
								<ul>
									<li>Ensuring you have permission to connect all linked accounts</li>
									<li>Reviewing and verifying all financial insights and recommendations</li>
									<li>Making your own informed financial decisions</li>
									<li>Maintaining compliance with your banks' terms of service</li>
								</ul>
							</section>

							{/* Intellectual Property */}
							<section className="mb-5">
								<h2 className="h3 mb-3">7. Intellectual Property Rights</h2>

								<h4 className="h5 mb-2">7.1 Our Property</h4>
								<p>Banking Intelligence owns all rights to:</p>
								<ul>
									<li>The Service software, algorithms, and technology</li>
									<li>Our AI models and machine learning systems</li>
									<li>The Banking Intelligence brand, trademarks, and logos</li>
									<li>All content and materials provided through the Service</li>
								</ul>

								<h4 className="h5 mb-2 mt-4">7.2 Your Data</h4>
								<p>
									You retain ownership of your personal and financial data. However, you grant us a license to use
									this data as necessary to provide the Service, as outlined in our Privacy Policy.
								</p>

								<h4 className="h5 mb-2 mt-4">7.3 Feedback and Suggestions</h4>
								<p>
									Any feedback, suggestions, or ideas you provide about the Service become our property and may be
									used to improve our offerings without compensation to you.
								</p>
							</section>

							{/* Payment and Billing */}
							<section className="mb-5">
								<h2 className="h3 mb-3">8. Payment and Billing</h2>

								<h4 className="h5 mb-2">8.1 Service Fees</h4>
								<p>
									Some features of our Service may require payment. Current pricing is available on our website.
									By subscribing to paid features, you agree to pay all applicable fees.
								</p>

								<h4 className="h5 mb-2 mt-4">8.2 Billing and Refunds</h4>
								<ul>
									<li>Fees are billed in advance on a monthly or annual basis</li>
									<li>All fees are non-refundable except as required by law</li>
									<li>We may change pricing with 30 days' notice</li>
									<li>Failure to pay may result in service suspension</li>
								</ul>

								<h4 className="h5 mb-2 mt-4">8.3 Free Trial</h4>
								<p>
									We may offer free trials of premium features. At the end of the trial period, you will be charged
									unless you cancel before the trial expires.
								</p>
							</section>

							{/* Service Availability */}
							<section className="mb-5">
								<h2 className="h3 mb-3">9. Service Availability and Modifications</h2>

								<h4 className="h5 mb-2">9.1 Service Uptime</h4>
								<p>
									We strive to maintain high service availability but cannot guarantee uninterrupted access.
									The Service may be unavailable due to maintenance, updates, or technical issues.
								</p>

								<h4 className="h5 mb-2 mt-4">9.2 Service Changes</h4>
								<p>We reserve the right to:</p>
								<ul>
									<li>Modify, suspend, or discontinue any part of the Service</li>
									<li>Update our AI models and algorithms</li>
									<li>Change features, functionality, or user interfaces</li>
									<li>Implement new security measures or requirements</li>
								</ul>
							</section>

							{/* Privacy and Data Protection */}
							<section className="mb-5">
								<h2 className="h3 mb-3">10. Privacy and Data Protection</h2>
								<p>
									Your privacy is important to us. Our collection, use, and protection of your information is governed
									by our Privacy Policy, which is incorporated into these Terms by reference.
								</p>
								<p>
									By using the Service, you consent to the collection and use of your information as described in
									our Privacy Policy.
								</p>
							</section>

							{/* Third-Party Services */}
							<section className="mb-5">
								<h2 className="h3 mb-3">11. Third-Party Services and Integrations</h2>

								<h4 className="h5 mb-2">11.1 Plaid Integration</h4>
								<p>
									Our Service uses Plaid to connect to your financial institutions. Your use of Plaid is subject to
									<a href="https://plaid.com/legal/#end-user-privacy-policy" target="_blank" rel="noopener noreferrer">Plaid's Privacy Policy</a> and
									<a href="https://plaid.com/legal/#end-user-terms" target="_blank" rel="noopener noreferrer">End User Terms</a>.
								</p>

								<h4 className="h5 mb-2 mt-4">11.2 Other Third-Party Services</h4>
								<p>
									We may integrate with other third-party services. We are not responsible for the content, privacy
									practices, or terms of service of these third parties.
								</p>
							</section>

							{/* Disclaimers and Limitations */}
							<section className="mb-5">
								<h2 className="h3 mb-3">12. Disclaimers and Limitation of Liability</h2>

								<h4 className="h5 mb-2">12.1 Service Disclaimers</h4>
								<div className="alert alert-warning">
									<p><strong>THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND.</strong> We disclaim all warranties, including but not limited to:</p>
									<ul>
										<li>Accuracy, completeness, or reliability of financial data or insights</li>
										<li>Uninterrupted or error-free service operation</li>
										<li>Fitness for a particular purpose or merchantability</li>
										<li>Security against unauthorized access or data breaches</li>
									</ul>
								</div>

								<h4 className="h5 mb-2 mt-4">12.2 Limitation of Liability</h4>
								<p>
									TO THE MAXIMUM EXTENT PERMITTED BY LAW, BANKING INTELLIGENCE SHALL NOT BE LIABLE FOR ANY
									INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES, INCLUDING BUT NOT LIMITED TO:
								</p>
								<ul>
									<li>Financial losses resulting from reliance on our insights or recommendations</li>
									<li>Damages from service interruptions or data loss</li>
									<li>Costs of obtaining substitute services</li>
									<li>Loss of profits, revenue, or business opportunities</li>
								</ul>
								<p>
									Our total liability to you for any claims shall not exceed the amount you paid us in the
									12 months preceding the claim.
								</p>
							</section>

							{/* Indemnification */}
							<section className="mb-5">
								<h2 className="h3 mb-3">13. Indemnification</h2>
								<p>
									You agree to indemnify and hold harmless Banking Intelligence from any claims, damages, or expenses
									(including reasonable attorney fees) arising from:
								</p>
								<ul>
									<li>Your violation of these Terms</li>
									<li>Your use of the Service</li>
									<li>Your violation of any rights of another party</li>
									<li>Any unauthorized use of your account</li>
								</ul>
							</section>

							{/* Governing Law */}
							<section className="mb-5">
								<h2 className="h3 mb-3">14. Governing Law and Dispute Resolution</h2>

								<h4 className="h5 mb-2">14.1 Governing Law</h4>
								<p>
									These Terms are governed by the laws of [Your State/Country], without regard to conflict of law principles.
								</p>

								<h4 className="h5 mb-2 mt-4">14.2 Dispute Resolution</h4>
								<p>
									Any disputes arising from these Terms or the Service shall be resolved through binding arbitration
									in accordance with the rules of the American Arbitration Association, except for claims that may be
									brought in small claims court.
								</p>

								<h4 className="h5 mb-2 mt-4">14.3 Class Action Waiver</h4>
								<p>
									You agree that any dispute resolution proceeding will be conducted only on an individual basis and
									not in a class, consolidated, or representative action.
								</p>
							</section>

							{/* General Provisions */}
							<section className="mb-5">
								<h2 className="h3 mb-3">15. General Provisions</h2>

								<h4 className="h5 mb-2">15.1 Entire Agreement</h4>
								<p>
									These Terms, together with our Privacy Policy, constitute the entire agreement between you and
									Banking Intelligence regarding the Service.
								</p>

								<h4 className="h5 mb-2 mt-4">15.2 Severability</h4>
								<p>
									If any provision of these Terms is found to be unenforceable, the remaining provisions will remain
									in full force and effect.
								</p>

								<h4 className="h5 mb-2 mt-4">15.3 Waiver</h4>
								<p>
									Our failure to enforce any provision of these Terms does not constitute a waiver of our right to
									enforce that provision in the future.
								</p>

								<h4 className="h5 mb-2 mt-4">15.4 Assignment</h4>
								<p>
									You may not assign or transfer your rights under these Terms without our consent. We may assign
									these Terms without restriction.
								</p>
							</section>

							{/* Changes to Terms */}
							<section className="mb-5">
								<h2 className="h3 mb-3">16. Changes to Terms</h2>
								<p>
									We may modify these Terms at any time. When we make changes, we will:
								</p>
								<ul>
									<li>Post the updated Terms on our website</li>
									<li>Update the "Last Updated" date</li>
									<li>Notify you via email or through the Service</li>
									<li>Provide at least 30 days' notice for material changes</li>
								</ul>
								<p>
									Your continued use of the Service after the effective date of revised Terms constitutes acceptance
									of the changes.
								</p>
							</section>

							{/* Contact Information */}
							<section className="mb-5">
								<h2 className="h3 mb-3">17. Contact Information</h2>
								<p>If you have questions about these Terms, please contact us:</p>
								<div className="contact-info mt-3">
									<p><strong>Email:</strong> legal@bankingintelligence.com</p>
									<p><strong>Address:</strong> Banking Intelligence<br />
										Legal Department<br />
										[Your Company Address]</p>
								</div>
								<p className="mt-3">
									We will respond to your inquiry within 10 business days.
								</p>
							</section>

							{/* Effective Date */}
							<section className="mb-0">
								<div className="alert alert-info">
									<p className="mb-0">
										<strong>Effective Date:</strong> These Terms of Service are effective as of {lastUpdated} and
										apply to all users of the Banking Intelligence service.
									</p>
								</div>
							</section>
						</div>
					</Card.Body>
				</Card>
			</Container>
		</div>
	);
};

export default TermsOfServicePage;